import type { Lesson, LessonSession } from '../types';

/** Minimal lesson fields for pricing one booking line (public + internal booking creation). */
export type BookingLineLessonPrice = Pick<
  Lesson,
  | 'lessonType'
  | 'pricingType'
  | 'pricePerSession'
  | 'price'
  | 'campDayPrice'
  | 'campFullPrice'
  | 'sessionPrice'
>;

export type PublicBookingLineOptions = {
  /** YYYY-MM-DD calendar day for this line (camp day, or per-session non-camp). */
  sessionDate?: string | null;
  /** When callers already loaded a materialized session, pass it so pricing uses `sessionDate` from the row. */
  lessonSession?: Pick<LessonSession, 'sessionDate'> | null;
  /** Opaque id for logging / future use; pricing still uses sessionDate from `lessonSession` when provided. */
  sessionId?: string | null;
};

/**
 * Amount for a single booking row when creating a public booking.
 * - Camp + sessionDate: per-day (`campDayPrice`).
 * - Camp without sessionDate: full package (`campFullPrice`, then `price`).
 * - Group + sessionDate + sessionPrice: drop-in per session.
 * - Non-camp: existing per-session / total behaviour.
 */
/** Whether staff calendar enroll can use session/day scope with a defined per-session list price. */
export function hasPerSessionStaffEnrollmentPrice(lesson: BookingLineLessonPrice): boolean {
  if (lesson.lessonType === 'camp') {
    return lesson.campDayPrice != null;
  }
  if (lesson.sessionPrice != null) {
    return true;
  }
  if (lesson.pricingType === 'per_session' && lesson.pricePerSession != null) {
    return true;
  }
  return false;
}

export function computePublicBookingLineAmount(
  lesson: BookingLineLessonPrice,
  options: PublicBookingLineOptions
): number {
  const sessionDate =
    options.sessionDate ??
    (options.lessonSession?.sessionDate ? options.lessonSession.sessionDate.slice(0, 10) : null);

  if (lesson.lessonType === 'camp') {
    if (sessionDate) {
      return lesson.campDayPrice ?? 0;
    }
    return lesson.campFullPrice ?? lesson.price ?? 0;
  }
  if (
    (lesson.lessonType === 'group' || lesson.lessonType === 'private') &&
    sessionDate &&
    lesson.sessionPrice != null
  ) {
    return lesson.sessionPrice;
  }
  if (
    sessionDate &&
    lesson.pricingType === 'per_session' &&
    lesson.pricePerSession != null
  ) {
    return lesson.pricePerSession;
  }
  return lesson.price ?? 0;
}
